import { useState } from "react";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/formatters";
import type { BreakdownEntry } from "@/api/analytics";

/** Default cap for bar-style breakdowns. Long-tail dimensions like
 *  "sector" routinely ship 50+ entries; surfacing them all at once
 *  buries the page. The operator can opt into the full list — see
 *  `showAll` in BarBreakdown. */
const BAR_BREAKDOWN_DEFAULT_LIMIT = 10;

interface BreakdownChartsProps {
  bySource?: BreakdownEntry[];
  bySector?: BreakdownEntry[];
  byFounderOrigin?: BreakdownEntry[];
  loading?: boolean;
}

const PALETTE = [
  "#6366f1",
  "#10b981",
  "#f59e0b",
  "#f43f5e",
  "#06b6d4",
  "#8b5cf6",
  "#84cc16",
  "#ec4899",
];

export function BreakdownCharts({
  bySource,
  bySector,
  byFounderOrigin,
  loading,
}: BreakdownChartsProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="h-[260px] animate-pulse rounded-plane bg-slate-100"
          />
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <Donut title="By source" entries={bySource ?? []} />
      <BarBreakdown title="By sector" entries={bySector ?? []} />
      <BarBreakdown
        title="By founder origin"
        entries={byFounderOrigin ?? []}
        showConversionRate
      />
    </div>
  );
}

function Donut({
  title,
  entries,
}: {
  title: string;
  entries?: BreakdownEntry[];
}) {
  const safe = entries ?? [];
  const total = safe.reduce((acc, e) => acc + (e.count ?? 0), 0);
  const data = safe.map((e, i) => ({
    name: e.label ?? e.key ?? `Item ${i + 1}`,
    value: e.count ?? 0,
    fill: PALETTE[i % PALETTE.length],
    keyId: e.key ?? `entry-${i}`,
  }));
  return (
    <section className="flex h-full flex-col rounded-plane bg-white p-5 shadow-plane">
      <h3 className="mb-3 text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {title}
      </h3>
      {data.length === 0 ? (
        <p className="text-sm italic text-slate-400">No data.</p>
      ) : (
        <>
          <div className="h-[160px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  innerRadius={48}
                  outerRadius={70}
                  paddingAngle={2}
                  isAnimationActive
                >
                  {data.map((d, i) => (
                    <Cell key={`${d.keyId}-${i}`} fill={d.fill} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v: number, n: string) => [
                    `${formatNumber(v)} (${total > 0 ? Math.round((v / total) * 100) : 0}%)`,
                    n,
                  ]}
                  contentStyle={{
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                    fontSize: 12,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-2 space-y-1 text-xs">
            {data.map((d, i) => (
              <li
                // Names can collide (or be missing); pair the
                // (server-provided) key with the index.
                key={`${d.keyId}-${i}`}
                className="flex items-center justify-between gap-2"
              >
                <span className="flex items-center gap-1.5 truncate text-slate-600">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: d.fill }}
                    aria-hidden
                  />
                  <span className="truncate">{d.name}</span>
                </span>
                <span className="tabular-nums text-slate-500">
                  {formatNumber(d.value)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function BarBreakdown({
  title,
  entries,
  showConversionRate = false,
}: {
  title: string;
  entries?: BreakdownEntry[];
  showConversionRate?: boolean;
}) {
  const safe = entries ?? [];
  const sorted = [...safe].sort(
    (a, b) => (b.count ?? 0) - (a.count ?? 0),
  );
  const max = Math.max(1, ...sorted.map((e) => e.count ?? 0));
  const [showAll, setShowAll] = useState(false);
  const overLimit = sorted.length > BAR_BREAKDOWN_DEFAULT_LIMIT;
  // Cap the rendered list at the default limit until the operator
  // opts in. When they do, swap the static <ol> for a scrollable
  // container so the card can't blow past the rest of the page.
  const visible = showAll
    ? sorted
    : sorted.slice(0, BAR_BREAKDOWN_DEFAULT_LIMIT);
  return (
    <section className="rounded-plane bg-white p-5 shadow-plane">
      <h3 className="mb-3 text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {title}
      </h3>
      {sorted.length === 0 ? (
        <p className="text-sm italic text-slate-400">No data.</p>
      ) : (
        <>
          <ol
            className={cn(
              "space-y-2.5",
              showAll && "max-h-[400px] overflow-y-auto pr-1",
            )}
          >
            {visible.map((e, i) => {
              const count = e.count ?? 0;
              const widthPct = Math.max(4, (count / max) * 100);
              const fill = PALETTE[i % PALETTE.length];
              return (
                <li key={e.key ?? `entry-${i}`}>
                  <div className="flex items-baseline justify-between text-xs">
                    <span className="truncate font-medium text-slate-700">
                      {e.label}
                    </span>
                    <span className="tabular-nums text-slate-500">
                      {formatNumber(count)}
                      {showConversionRate &&
                        e.conversion_rate != null &&
                        Number.isFinite(e.conversion_rate) && (
                          <span className="ml-2 text-emerald-600">
                            {`${(Math.round(e.conversion_rate * 1000) / 10).toFixed(1)}%`}
                          </span>
                        )}
                    </span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-slate-100">
                    <div
                      className={cn("h-full rounded-full transition-[width]")}
                      style={{
                        width: `${widthPct}%`,
                        backgroundColor: fill,
                      }}
                    />
                  </div>
                </li>
              );
            })}
          </ol>
          {overLimit && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-3 text-xs font-medium text-indigo-500 transition-colors hover:text-indigo-700"
            >
              {showAll
                ? `Show top ${BAR_BREAKDOWN_DEFAULT_LIMIT}`
                : `Show all ${sorted.length} ${title.replace(/^by\s+/i, "").toLowerCase()}s`}
            </button>
          )}
        </>
      )}
    </section>
  );
}
